import { Timestamp } from 'firebase/firestore';

export const MILESTONE_STATUSES = ['pending', 'done', 'late'] as const;
export type MilestoneStatus = (typeof MILESTONE_STATUSES)[number];

/**
 * FV (Fiche de Validation) states — mirrors the PM Excel dashboard.
 * These are the approval states used for deliverables at each review.
 */
export const FV_STATUSES = [
  'pass',
  'derogated',
  'not-pass',
  'validated',
  'refused',
  'sent',
  'not-done',
  'na',
] as const;
export type FvStatus = (typeof FV_STATUSES)[number];

/** Legacy simple checklist statuses (kept for backward compat with Phase 1-3 data) */
export const CHECKLIST_STATUSES = ['pending', 'done', 'na'] as const;
export type ChecklistStatus = (typeof CHECKLIST_STATUSES)[number];

export interface ChecklistItem {
  what: string;
  who: string;
  status: ChecklistStatus;
  date?: Timestamp;
  comment?: string;
}

/** Décision de revue — le champ « Kick-Off OK => Go for Architecture » du PAD. */
export const MILESTONE_DECISIONS = ['pending', 'pass', 'derogated', 'not-pass'] as const;
export type MilestoneDecision = (typeof MILESTONE_DECISIONS)[number];

/**
 * Risk assessment captured at a review.
 * The PM Excel asks "IS MY PROJECT RISKY AT THIS STAGE?" at 4 reviews — carried
 * as an `FvDeliverable` row, not a dedicated field.
 */
export type RiskLevel = 'yes' | 'no' | 'na';

/**
 * A Fiche de Validation (FV) deliverable — the core unit of the review.
 * Each review has a set of FVs that must be signed off before it can pass.
 */
export interface FvDeliverable {
  /** Libellé PAD, ex. "FV Design", "IS MY PROJECT RISKY AT THIS STAGE ?" */
  name: string;
  status: FvStatus;
  /** URL du document dans le QMS SharePoint — jamais un fichier stocké. */
  link?: string;
  /** uid du valideur */
  validatedBy?: string;
  /** When the status was last changed */
  date?: Timestamp;
  comment?: string;
}

/**
 * Un jalon = un point de contrôle daté. Quand il porte un `reviewName`, c'est
 * une revue formelle avec ses fiches de validation ; sinon, une simple échéance.
 *
 * Le PAD définit 6 revues pour ScaleUp (Kick-Off, Architecture, PF, DFM, PR, SR)
 * réparties sur 11 échéances. LaunchPad/UnLock restent en J1–J4, sans fiche.
 */
export interface Milestone {
  id: string;
  name: string;
  /** Clé stable du gabarit (`sop`, `fv-design`…) — clé de dédoublonnage de l'import PAD. */
  templateId?: string;
  /** Legacy checklist plate — LaunchPad/UnLock, conservée telle quelle. */
  checklist: ChecklistItem[];
  /** Date cible initiale (colonne `… target`). */
  targetDate?: Timestamp;
  /** Date replanifiée (colonne `… rescheduled`) — fait foi sur `targetDate` si présente. */
  rescheduledDate?: Timestamp;
  validationDate?: Timestamp;
  status: MilestoneStatus;
  order: number;
  /** Présent = ce jalon porte une revue formelle. Absent = simple échéance datée. */
  reviewName?: string;
  deliverables?: FvDeliverable[];
  decision?: MilestoneDecision;
  decisionDate?: Timestamp;
  decidedBy?: string;
  comment?: string;
  createdAt: Timestamp;
  updatedAt: Timestamp;
}
